// The CLAN section of the hub (R229).
//
// The panel draws one of two screens, and one answer decides which:
//
//   - `GET /api/clan` answers 404 with `no_clan` for most accounts. That is
//     not a failure. It draws the FIND A CLAN screen: the browse box, the
//     clans the server lists, an APPLY control for each of them, and the
//     form that founds one.
//   - An account that is in a clan gets the roster, the applications an
//     officer answers, the squads and the global chat.
//
// The ROSTER is the people and the squads. It groups the members under the
// squads of the clan. Each squad heading shows how many people the squad holds, the
// world the assignment batch seated it in, and the Honour the batch ranked
// it by. The roster carries every control that acts on a person: the move
// between squads, the role, and the removal. An EDIT control on the row
// opens them, and the row's role stays a plain value (R257).
//
// What it reads live:
//   - `GET /api/clan` and `GET /api/clans?q=` — the clan half of the meta
//     API (R228, `net/api.ts`).
//   - `WS /api/clan/chat` — the global clan chat (R222, and
//     R225 for the one-socket rule; `net/clanchat.ts`).
//   - `GET /api/pickup-squad` and `GET /api/invites` — the two invite cards
//     of `pickup.ts` (R1306), which run their writes through this panel.
//
// What it writes: the clan routes of R228 and R240 — apply, create, approve,
// refuse, leave, remove, role, join policy, and the one squad route that
// moves a member. Every rule behind them is the server's. This panel hides a
// control the caller's role does not carry, and the server still refuses the
// call when the panel is wrong.
//
// A clan flies one faction (R240). The founder picks it on the create form,
// the clan hands the banner to every member it takes in, and nothing changes
// it after that. The panel prints a faction in the colour `world.ts` gives
// it. That table is the map's own legend, and it is not a theme token.
//
// The banner stands on the second line of the heading (R257, R1562). It
// belongs to the clan and not to a card: every member sees it there, and a
// plain member never gets the APPLICATIONS card that carries the join-policy
// pill group.
//
// A clan also carries a join policy (R240). `open` takes an applicant in
// inside the apply call, `invite` writes an application an officer answers,
// and `closed` turns every application away. An officer or the owner sets it.
//
// What is honestly empty today:
//   - A clan carries no motto and no Honour of its own. The mockup put a
//     clan rank beside a name in the browse list. No route answers one, so
//     this panel draws no rank there.
//   - An application carries a name, a note and an instant, and nothing
//     else. The mockup's Honour figure beside an applicant is not on the
//     wire.
//   - A squad has no name. Every clan holds the squads of `SQUAD_COUNT` from
//     the moment it is founded, and no control makes or removes one. Every
//     member holds one: the server puts a new member in a squad at the
//     moment they join, so no control takes a member out of one. A clan above
//     a lowered figure also holds old squads, and the roster draws each one
//     that holds a member (R2974).
//   - The squad plan applies at the NEXT season start. Nothing here moves a
//     commander who is already at sea, and the card says so.
//   - A squad carries a seat only after the assignment batch has run. The
//     batch runs at the season roll, and the card says that, so a squad with
//     no seat reads as a schedule and not as a fault.
//   - A deployment that serves no chat closes the socket with 4103. The
//     card says that, rather than turning forever.
//
// The CLAN WAR card is the registration for the next Clan War run
// (numbers.md §14.4, R1263, R1264): `GET /api/clan/clanwar` answers the run,
// its cut-off, the clan's list and what the batch decided. An owner or an
// officer picks the members who fight and presses REGISTER; the count sets
// the division. The card is the clan's notice of its banner, and of a run
// with no opponent.
//
// The chat socket belongs to this panel and to nothing else. `main.ts`
// opens the in-game chat socket after the hub resolves, so `destroy()`
// closes this one first: two sockets on one clan channel is one too many.
//
// **The section shell** (`../sectionshell.ts`, ui-map-interface.md §16.11
// and §16.16, card 04 of mockup round 092). A clan holds these sections:
// CHAT, MEMBERS (APPLICATIONS, ROSTER and LAST SEASON'S SQUAD), CLAN WAR,
// CLAN CUP (the cup of now and the CUP RECORD, with the CUP page as a
// sub-page under ‹ CLAN CUP) and, for an officer and the owner, DISCORD
// and CHAT RELAY, two sections of their own (R1945). A page draws no card
// round its body: one block stands alone under the page title, and two or
// more stand each under a sub-header (`blocksHtml`, §16.16 "The page
// body"). The chat is the companion column of every
// other page (fold `rail`): the CHAT row stands in the rail only while the
// column cannot, and on a telephone CHAT is the first tile, the width of
// both columns. One chat model feeds the column, the CHAT page and the
// summary, so the socket never depends on which one draws. An account in
// no clan holds INVITES (while it holds one), FIND A CLAN and START ONE,
// with LAST SEASON'S SQUAD as the companion column. START ONE holds the
// founding form in place (R2155, `clanfound.ts`): the faction cards, the
// founding chart, and the name, the tag and FOUND IT.
//
// **The hours match** (R1908, player-reach.md §5.5). FIND A CLAN opens on
// CLANS OF YOUR HOURS: `GET /api/clans/hours` answers the clans whose
// members state the reader's hours, in the server's order, and the FIND A
// CLAN row states how many match. The server holds the rule and gives no
// figure of one clan, so this panel draws the order it got and nothing
// more. A reader with no stated hours gets the ask and a press that opens
// SETTINGS · PLAY; a read that fails draws no card, because the browse
// list under it still finds every clan.

import {
  ApiError, CLAN_ROLES, CODE_CLAN_CLOSED, CODE_CLAN_FULL, CODE_NO_CLAN,
  JOIN_POLICIES, SQUAD_COUNT,
  applyToClan, approveApplication, assignSquad, browseClans, createClan,
  getClan, getClanWar, leaveClan, matchClanHours, messageOf, putBlock, refuseApplication,
  registerClanWar, removeMember, setJoinPolicy, setRole, withdrawClanWar,
  type Clan, type ClanHoursMatch, type ClanMember, type ClanRole, type ClanSquad, type ClanWar,
  type JoinPolicy, type MyClan,
} from '../../../net/api';
import {
  CHAT_MAX_LEN,
  CLAN_HOURS_MIN_MEMBERS,
  CLAN_NOTE_RUNES,
  CLAN_NAME_MAX_LEN as CLAN_NAME_MAX,
  CLAN_NAME_MIN_LEN as CLAN_NAME_MIN,
  CLAN_TAG_MAX_LEN as CLAN_TAG_MAX,
  CLAN_TAG_MIN_LEN as CLAN_TAG_MIN,
  MAX_CLAN_MEMBERS as CLAN_MAX_MEMBERS,
} from '../../../gen/balance';
// The arm window rides presentation.toml [presentation.chrome]: it is
// the same four seconds the overlay's LOG OUT control waits (R312).
import { CHROME_ARM_MS } from '../../../gen/presentation';
import { ArmGate } from '../../arm';
import { SFX_CLAN_HAIL } from '../../../audio/triggers';
import { createInviteCards, inviteRefusalFor, type InviteCards, type RunCall } from './pickup';
import { createDiscordCard, discordRefusalFor, type DiscordCard, type DiscordCardHooks } from './discord';
import { chatRelayRefusalFor, createChatRelayCard, type ChatRelayCard } from './chatrelay';
import { relayMark as RM } from '../../../gen/strings';
import { button, pillGroup, pillMulti, tag } from '../../choice/choice';
import { FACTION_ACT, foundingBody, wireFactionWalk } from './clanfound';
import { createCupCards, type CupCards } from './cup';
import { cupMarkFor, onCupHolder } from '../../cupmark';
import { chatStampHtml, storedChatTimeOn } from '../../chatstamp';
import { chatText, storedChatFilterOn } from '../../chatfilter';
import { createReportForm, type ReportForm } from './report';
import { BLOCKS_CELL, blocksCell } from './blocks';
import {
  ClanChatConnection, ClanChatState, HUB_CHAT_TAIL, clanChatUrl, type ClanChatEntry, type ClanChatMessage,
  type ClanChatStop,
} from '../../../net/clanchat';
import { FACTIONS, factionColor, factionName, factionOf, hex } from '../../../world';
import { hubClan as S, hubHours, hubPickup } from '../../../gen/strings';
import {
  CLANWAR_CELL, TTL_ON_OPEN, clanDot, formatAgo, formatSpan, groupDigits, secondsUntil, type Cell,
} from '../hubstate';
import {
  armedButton, armedPress, ctlRow, empty, errLine, esc, factionTag, failed, figLine, h1, lede, loading, page,
  pending, plain, rows, section, textBox, field, wireOnce,
  PAGE_CUP, type PageKey, type Panel, type PanelContext, type Row,
} from '../panel';
import {
  SectionNav, shellHtml, shellPress, type Companion, type ShellPage, type ShellSection,
} from '../sectionshell';

// The clan name and tag rule and the roster cap ride
// data/product/meta.toml [meta.social] (R351). The meta server enforces
// the same generated figures, and the refusal line below reads them
// rather than spelling them out in the sentence.

/** The cache key of `GET /api/clan`. The HOURS card reads the same cell,
 *  to learn whether the account is in a clan (R1319). */
const CLAN_CELL = 'clan';

/** The cache key of `GET /api/clans/hours` (R1908). A write of the stated
 *  hours drops it (`hours.ts`). */
export const CLAN_HOURS_CELL = 'clan-hours';

/** The hours match of FIND A CLAN, through the hub cache. */
function clanHoursCell(ctx: Pick<PanelContext, 'data'>): Cell<ClanHoursMatch> {
  return ctx.data.cell<ClanHoursMatch>(CLAN_HOURS_CELL, TTL_ON_OPEN, matchClanHours);
}

/** The name of a stated band, from the words of the STATED HOURS. A band
 *  this build does not know keeps the server's word. */
const BAND_NAMES: ReadonlyMap<string, string> = new Map([
  ['morning', hubHours.band_morning],
  ['afternoon', hubHours.band_afternoon],
  ['evening', hubHours.band_evening],
  ['night', hubHours.band_night],
]);

/** The summary of the FIND A CLAN row: the count of the hours match, or ''
 *  while the read travels, when it failed and for a reader with no stated
 *  hours (R1908). */
export function findSummary(read: Cell<ClanHoursMatch>): string {
  const m = read.value;
  if (read.state !== 'ready' || m === undefined || m.hours === '') return '';
  return S.sum_find_hours({ n: m.total });
}

/** The sections of a clan, in the rail order (§16.11). DISCORD and CHAT
 *  RELAY stand for an officer and the owner alone; the chat relay is a
 *  section of its own, so the two pages about Discord carry two names
 *  (R1945). */
export const CLAN_SECTIONS = ['chat', 'members', 'war', 'cup', 'discord', 'relay'] as const;
/** The sections of an account in no clan. INVITES stands while the
 *  account holds an open invite. */
const CLANLESS_SECTIONS = ['invites', 'find', 'start'] as const;
type ClanSection = (typeof CLAN_SECTIONS)[number] | (typeof CLANLESS_SECTIONS)[number];
const SECTION_IDS: readonly ClanSection[] = [...CLAN_SECTIONS, ...CLANLESS_SECTIONS];

/** The old words of the cards that a section now holds: an address that
 *  names a card opens its section. */
const CLAN_ALIASES: Readonly<Record<string, ClanSection>> = {
  roster: 'members',
  applications: 'members',
  squads: 'members',
  clanwar: 'war',
  clancup: 'cup',
  browse: 'find',
  clans: 'find',
  create: 'start',
};

/** One block of a page: the name of its sub-header, the quiet second
 *  text (a count) and the content. */
interface Block {
  title: string;
  aside: string;
  inner: string;
  cls?: string;
}

/** The body of a page from its blocks (R1945, ui-map-interface.md §16.16
 *  "The page body"): one block draws alone, with no box and no heading,
 *  because the page title names it; two or more draw each under its
 *  sub-header. A null block is one the reader does not see. */
function blocksHtml(list: readonly (Block | null)[]): string {
  const shown = list.filter((b): b is Block => b !== null);
  if (shown.length === 1) return plain(shown[0].inner, shown[0].cls);
  return shown.map((b) => section(b.title, b.aside, b.inner, b.cls)).join('');
}

/** The press of a rail row and of a tile, and ‹ CLAN of a narrow page. */
const GO = 'clan-sec';
const BACK = 'clan-back';
const SHELL = { go: GO, back: BACK };

/** The account's clan, through the hub cache. The 404 `no_clan` of an
 *  account in no clan is a failed cell, which `isNoClan` tells apart. */
export function clanCell(ctx: Pick<PanelContext, 'data'>): Cell<MyClan> {
  return ctx.data.cell<MyClan>(CLAN_CELL, TTL_ON_OPEN, getClan);
}

/** The squad numbers a member can take, 1 to `SQUAD_COUNT`. They do not come
 *  from the answer, because an old squad above the figure takes nobody. */
const SQUAD_IDX: number[] = Array.from({ length: SQUAD_COUNT }, (_, i) => i + 1);

/** A role as a rank. Index 0 outranks index 2. */
function rankOf(role: ClanRole): number {
  const i = CLAN_ROLES.indexOf(role);
  return i < 0 ? CLAN_ROLES.length : i;
}

/** True while `mine` is above `theirs`. */
function outranks(mine: ClanRole, theirs: ClanRole): boolean {
  return rankOf(mine) < rankOf(theirs);
}

/** True for a role that manages the clan: an officer or the owner. */
function manages(role: ClanRole): boolean {
  return rankOf(role) <= rankOf('officer');
}

/** True while a clan holds no room for one more member. */
function isFull(c: Clan): boolean {
  return c.members >= c.maxMembers;
}

/** True for a faction the game has, 1 to 5. A clan the meta server wrote
 *  before R240 carries 0 or nothing at all, and that is not a faction. */
function hasFaction(id?: number): boolean {
  return id !== undefined && factionOf(id) !== undefined;
}

/** One faction as a label, for a clan that already flies one. A clan with
 *  no faction on record says so, rather than reading as faction 1. */
function factionLabel(id?: number): string {
  const f = id ?? 0;
  return hasFaction(f) ? factionTag(f) : tag(S.no_banner_on_record);
}

/**
 * The controls of one open roster row, as one aligned horizontal group
 * (R257).
 *
 * The controls are three runs, and the order is fixed: the squad moves, then
 * the role, then the destructive pair. A run that holds nothing is dropped.
 * The runs keep their places, so the same control sits under the same part
 * of the row on every member. A loose pill cloud does not do that, and a
 * player who opens two rows in turn must not hunt for REMOVE twice.
 */
function editRow(moves: string, role: string, gone: string): string {
  const run = (cls: string, inner: string): string => (
    inner === '' ? '' : `<div class="grp ${cls}">${inner}</div>`
  );
  return `<div class="editrow">${run('moves', moves)}${run('role', role)}`
    + `${run('gone', gone)}</div>`;
}

/** What the panel says about one join policy: the control's label and why
 *  the applications card is empty under it. The wire value stays the wire
 *  value; only the words a reader sees come from the locale. A policy this
 *  build does not know reads as `invite`, the way `policyOf` reads an
 *  absent one. */
type PolicyWords = { label: string; none: string };
const INVITE_WORDS: PolicyWords = { label: S.policy_label_invite, none: S.apps_none_invite };
/** The server sends the policy, so the table is a `Map` (R1341). */
const POLICY_WORDS: ReadonlyMap<string, PolicyWords> = new Map([
  ['open', { label: S.policy_label_open, none: S.apps_none_open }],
  ['invite', INVITE_WORDS],
  ['closed', { label: S.policy_label_closed, none: S.apps_none_closed }],
]);

function policyWords(policy: JoinPolicy): PolicyWords {
  return POLICY_WORDS.get(policy) ?? INVITE_WORDS;
}

/** The three roles as a rank a reader sees. */
const ROLE_LABELS: ReadonlyMap<string, string> = new Map([
  ['owner', S.role_owner],
  ['officer', S.role_officer],
  ['member', S.role_member],
]);

/** One role as a rank a reader sees. A role this build does not know
 *  keeps the name the server sent, so nobody's row goes blank. */
function roleLabel(role: ClanRole): string {
  return ROLE_LABELS.get(role) ?? String(role).toUpperCase();
}

/** The policy a clan runs. An absent value reads as `invite`, which is what
 *  the column defaults to and what every clan did before R240. */
function policyOf(clan: Clan): JoinPolicy {
  return clan.joinPolicy ?? 'invite';
}

/** The line of a Clan War registration the batch did not place, by its
 *  outcome. The server sends the word, so the table is a `Map` (R1341); a
 *  word this build does not know reads as waiting. */
const OUTCOME_LINES: ReadonlyMap<string, string> = new Map([
  ['no_opponent', S.cw_outcome_no_opponent],
  ['unplaced_no_seat', S.cw_outcome_no_world],
]);

/** The empty state the chat card shows for each way the socket stopped. */
const STOP_LINES: Record<ClanChatStop, [what: string, why: string]> = {
  unauthorized: [S.chat_refused, S.chat_refused_why],
  unavailable: [S.chat_unavailable, S.chat_unavailable_why],
  'no-clan': [S.chat_no_clan, S.chat_no_clan_why],
};

/** True for the 404 that says "this account is in no clan". It is the state
 *  most accounts are in, so the panel checks it before it calls anything a
 *  failure. The HOURS card of SETTINGS reads it too (R1563). */
export function isNoClan(err: unknown): boolean {
  return err instanceof ApiError && err.code === CODE_NO_CLAN;
}

/** The line of each typed refusal of a clan route, by the wire code. */
const REFUSALS: ReadonlyMap<string, string> = new Map([
  ['clan_taken', S.refusal_clan_taken],
  ['already_in_clan', S.refusal_already_in_clan],
  ['already_applied', S.refusal_already_applied],
  [CODE_CLAN_FULL, S.refusal_clan_full],
  // It is not `clan_full`: a closed clan may hold room and still take
  // nobody, and a player must be able to tell the two apart (R240).
  [CODE_CLAN_CLOSED, S.refusal_clan_closed],
  // Every figure in this line is a server rule, so every figure is read
  // from the rule and never typed into the sentence.
  ['bad_request', S.refusal_bad_request({
    name_min: CLAN_NAME_MIN, name_max: CLAN_NAME_MAX,
    tag_min: CLAN_TAG_MIN, tag_max: CLAN_TAG_MAX,
    factions: FACTIONS.length, squads: SQUAD_COUNT,
  })],
  ['owner_must_hand_over', S.refusal_owner_must_hand_over],
  ['forbidden', S.refusal_forbidden],
]);

/**
 * What the panel says about one typed refusal. The server's own message
 * goes beside it, because a person may read that and a client must not
 * parse it. An unknown code gets no sentence and keeps the message alone.
 * The invite routes (R1306), the webhook routes (R1311) and the relay
 * routes (R1641) carry codes of their own; a webhook refusal also reads the
 * field it names (R1642).
 */
function refusalFor(code: string, field: string): string {
  return REFUSALS.get(code)
    ?? (inviteRefusalFor(code) || discordRefusalFor(code, field) || chatRelayRefusalFor(code));
}

/** The redraw of the newest CLAN panel, which the cup mark answer calls.
 *  The hub builds a new panel on each visit, so the module subscribes
 *  once and a panel built twice stacks no refresh. */
let cupRedraw: (() => void) | null = null;
let cupSubscribed = false;

/** Points the one cup mark subscription at `fn`. */
function redrawOnCupHolder(fn: () => void): void {
  cupRedraw = fn;
  if (cupSubscribed) return;
  cupSubscribed = true;
  onCupHolder(() => cupRedraw?.());
}

/** The CLAN panel. */
class ClanPanel implements Panel {
  readonly key = 'clan' as const;

  /** The transcript. It belongs to this panel, and it outlives a section
   *  change so the dot keeps counting while another section is open. */
  private state = new ClanChatState();
  private chat: ClanChatConnection | null = null;
  private unsub: (() => void) | null = null;
  /** True while this is the open section. */
  private visible = false;
  /** How many live lines this panel has already announced with a cue. It is
   *  a count and not a flag, so a redraw never plays the cue again. */
  private announced = 0;

  /** The application ids this panel has already seen, or null before the
   *  first answer. It is a set and not a count, so an answered application
   *  and a new one in the same answer still read as one arrival (R550). */
  private hailed: Set<number> | null = null;

  /** What every text box holds, by its `data-in` name. The pane is rewritten
   *  whenever the markup changes, so the text lives here and the markup
   *  carries it back as a value. */
  private text: Record<string, string> = {};
  /** The browse query the list is drawn from. It changes on BROWSE and not
   *  on every key, because each query is a cell of its own. */
  private query = '';

  /** The member the roster card has open. Every control that acts on a
   *  person is behind this one pick, so there is one. The row's EDIT control
   *  toggles it, and it wears the open state (R257). */
  private pick = '';

  /** The faction the founder has picked on the create form, or 0 while they
   *  have picked none. A clan flies one banner for its whole life, so the
   *  panel sends no create call until the founder names it. */
  private faction = 0;

  /** The INVITE clan whose apply step stands open, or 0 for none (R1742).
   *  The note box stands in that step, under the row of the clan, and not
   *  in the FIND A CLAN card before a clan is picked. */
  private applying = 0;
  /** The list whose row holds the open apply step: the hours match or the
   *  browse list. A clan can stand in both, and the note box stands once. */
  private applyIn: 'hours' | 'browse' = 'browse';

  /** The members the CLAN WAR card has picked, by public id, or null while
   *  the officer has not touched the pick. Null draws the registered list,
   *  or the whole roster for a clan that has not registered. */
  private warPick: Set<string> | null = null;

  /** The two-press gate of every destructive control in this panel
   *  (`ui/arm.ts`, R696). It holds the armed key and the window: the
   *  four seconds the LOG OUT control of `ui/overlay.ts` waits. */
  private gate = new ArmGate(CHROME_ARM_MS, () => this.ctx.refresh());

  private busy = '';
  private err = '';
  private errCode = '';
  /** The JSON member the last refusal named, or ''. */
  private errField = '';
  /** The plain outcome of the last call, where the answer says something a
   *  refusal line cannot. An application that waits is the one case. */
  private notice = '';

  /** LAST SEASON'S SQUAD and INVITES (`pickup.ts`, R1306). They share this
   *  panel's write path, its arm gate and its refusal line. */
  private invites: InviteCards;

  /** DISCORD (`discord.ts`, R1311): the webhook of the clan, for the owner
   *  and the officers. It shares this panel's write path and arm gate. */
  private discord: DiscordCard;
  /** CHAT RELAY (`chatrelay.ts`, R1641): the copy of the clan chat to one
   *  Discord channel, for the owner and the officers. It shares this
   *  panel's write path, its arm gate and the hooks of DISCORD. */
  private relay: ChatRelayCard;
  /** The clan cup (`cup.ts`, R1629): the CLAN CUP card, the CUP page, the
   *  title line and the CUP RECORD. It shares this panel's write path and
   *  arm gate. */
  private cups: CupCards;
  /** The report form (`report.ts`, deployment.md §7.6): REPORT beside a
   *  chat line, a roster name and a clan in the clan list. */
  private reports: ReportForm;

  /** The chat line whose actions are open, by its sequence number, or 0.
   *  REPORT and BLOCK sit behind one pick, so a line of the log stays one
   *  line until the player asks for more. */
  private linePick = 0;

  /** The public ids this session blocked. The socket drops their next
   *  lines, and this set drops the lines already on the card at once. */
  private blocked = new Set<string>();

  /** The open section and the section the address named (§16.16). */
  private nav = new SectionNav({
    ids: () => SECTION_IDS,
    first: () => this.firstId,
    aliases: CLAN_ALIASES,
  });
  /** The section a wide pane opens on: MEMBERS for a clan, the first
   *  section of a clanless account. Each draw writes it. */
  private firstId: ClanSection = 'members';
  /** The section the last draw showed, or null for the tiles. */
  private drawn: ClanSection | null = null;
  /** True while the companion column of the chat stood on the screen at
   *  the last wire. The stylesheet decides it, so `wire` reads it. */
  private columnShown = false;
  /** The live lines of other people since the socket opened, counted up
   *  and never down, and the count when the chat last stood in view: the
   *  difference is the count of the CHAT row and tile. */
  private arrived = 0;
  private seenArrived = 0;
  /** The empty clan list sent the reader to START ONE: the next wire puts
   *  the focus in the name box. */
  private focusName = false;

  constructor(private ctx: PanelContext) {
    this.unsub = this.state.subscribe(() => this.onChat());
    this.reports = createReportForm(ctx);
    // The four cards share this panel's write path, its outcome line and
    // its boxes, through one set of hooks.
    const run: RunCall = (label, fn, done) => this.call(label, fn, done);
    const hooks: DiscordCardHooks = {
      run,
      notice: (text) => { this.notice = text; },
      typed: (name) => this.typed(name),
      box: (name, max, placeholder, label) => this.box(name, max, placeholder, label),
      clear: (name) => { this.text[name] = ''; },
      fill: (name, value) => { this.text[name] = value; },
    };
    this.invites = createInviteCards(ctx, this.gate, {
      run,
      notice: hooks.notice,
      joined: () => {
        this.ctx.data.dropPrefix('clans:');
        this.reopenChat();
      },
    });
    this.discord = createDiscordCard(ctx, this.gate, hooks);
    this.relay = createChatRelayCard(ctx, this.gate, hooks);
    this.cups = createCupCards(ctx, this.gate, { run });
    // The cup mark beside a tag comes from one read of the page; the pane
    // draws again when it answers (R1634).
    redrawOnCupHolder(this.cupRedraw);
  }

  /** The redraw the cup mark answer calls while this panel is the newest. */
  private readonly cupRedraw = (): void => this.ctx.refresh();

  // --- what the shell calls --------------------------------------------

  open(): void {
    this.visible = true;
    // Opening the section is what clears the dot of the dock (§16.4). The
    // CHAT row keeps its own count until the chat stands in view.
    this.state.markRead();
    this.nav.open();
    this.openChat();
    this.ctx.refresh();
  }

  close(): void {
    this.visible = false;
    this.gate.clear(false);
    this.nav.close();
    this.cups.hide();
  }

  /** The address named the CUP page (`?tab=clan&page=cup`, R1649): the
   *  sub-page of the CLAN CUP section. */
  openPage(page: PageKey): void {
    if (page !== PAGE_CUP) return;
    this.cups.show();
    this.nav.ask('cup');
  }

  /** The address named a section (`?tab=clan&section=war`, §16.16). */
  openSection(word: string): void {
    this.nav.ask(word);
  }

  /** The hub is going, and the socket goes with it. It closes here and not
   *  in `close()`: the dot must keep counting while another section is the
   *  open one. */
  destroy(): void {
    if (cupRedraw === this.cupRedraw) cupRedraw = null;
    this.gate.clear(false);
    this.unsub?.();
    this.unsub = null;
    this.chat?.close();
    this.chat = null;
  }

  dot(): boolean {
    return clanDot(this.state.unread);
  }

  // --- the chat socket --------------------------------------------------

  /**
   * Opens the socket, one time. It opens while the account is in a clan and
   * while the state is still unknown, because the first render happens
   * before `GET /api/clan` has answered. An account in no clan gets close
   * code 4101 and the socket stops itself. That is a state and not a
   * failure to show loudly.
   */
  private openChat(): void {
    if (this.chat !== null) return;
    const known = this.ctx.data.peek<MyClan>(CLAN_CELL);
    if (known.state === 'failed' && isNoClan(known.error)) return;
    this.chat = new ClanChatConnection(this.state.handlers(), clanChatUrl(HUB_CHAT_TAIL));
  }

  /** Opens a second socket after this account founded a clan. The first one
   *  stopped for good on 4101, and nothing in it reconnects. */
  private reopenChat(): void {
    if (this.chat !== null && this.chat.stopReason === null) return;
    this.chat?.close();
    this.announced = 0;
    this.chat = new ClanChatConnection(this.state.handlers(), clanChatUrl(HUB_CHAT_TAIL));
  }

  /** One change of the transcript: a line, a status or a refusal. */
  private onChat(): void {
    const live = this.liveCount();
    if (live > this.announced) {
      this.arrived += live - this.announced;
      this.announced = live;
      this.ctx.play('sfx-menu-chat-recv');
    } else if (live < this.announced) {
      // The transcript was trimmed at its cap. Nothing arrived.
      this.announced = live;
    }
    if (this.visible) this.state.markRead();
    if (this.chatInView()) this.seenArrived = this.arrived;
    this.ctx.refresh();
  }

  /** True while the reader can see the chat: the CHAT page is open, or
   *  the companion column stands. */
  private chatInView(): boolean {
    return this.visible && (this.drawn === 'chat' || this.columnShown);
  }

  /** The live lines of other people the reader has not had in view. */
  private newLines(): number {
    return Math.max(0, this.arrived - this.seenArrived);
  }

  /**
   * The live lines from other people. A replayed backlog line is not news,
   * and the account's own line already had the send cue, so neither one
   * counts here.
   */
  private liveCount(): number {
    let n = 0;
    for (const e of this.state.entries) {
      if (e.kind === 'message' && !e.history && e.senderId !== this.state.selfId) n++;
    }
    return n;
  }

  private send(): void {
    const body = (this.text.chat ?? '').trim();
    if (body === '') return;
    if (this.chat === null || !this.chat.say(body)) {
      this.ctx.play('sfx-menu-error');
      return;
    }
    this.text.chat = '';
    this.ctx.play('sfx-menu-chat-send');
    this.ctx.refresh();
  }

  // --- calls and refusals -----------------------------------------------

  /**
   * Runs one write with the panel in its busy state. A success throws the
   * clan cell away, so the next render reads the new truth. The answer
   * reaches `done`, because an apply answers two different things (R240).
   *
   * It throws the **account** cell away as well (R242). A clan write moves
   * the account's own clan, and founding one or joining one moves the
   * account's faction with it. The header names that faction and the AUTO
   * theme wears it, so both would keep the old banner until the cell ran
   * out on its own. The theme must follow the clan and never a clock.
   */
  private call<T>(label: string, fn: () => Promise<T>, done?: (out: T) => void): void {
    if (this.busy !== '') return;
    this.busy = label;
    this.err = '';
    this.errCode = '';
    this.errField = '';
    this.notice = '';
    this.ctx.refresh();
    fn().then(
      (out) => {
        this.busy = '';
        this.ctx.data.drop(CLAN_CELL);
        this.ctx.data.drop('me');
        done?.(out);
        this.ctx.refresh();
      },
      (e: unknown) => {
        this.busy = '';
        this.errCode = e instanceof ApiError ? e.code : '';
        this.errField = e instanceof ApiError ? e.field : '';
        this.err = messageOf(e);
        this.ctx.play('sfx-menu-error');
        this.ctx.refresh();
      },
    );
  }

  // --- rendering --------------------------------------------------------

  html(): string {
    const cell = clanCell(this.ctx);
    // The CUP page needs no clan: the deep link of a cup post opens it for
    // a reader in no clan, or in a clan with no cup team (R1649). Until the
    // clan has answered, and for a reader in none, it stands in place of
    // the clan screen under its own ‹ CLAN bar; in a clan it is a sub-page
    // of CLAN CUP.
    const known = cell.state === 'failed' ? undefined : cell.value;
    if (this.cups.pageOpen() && known === undefined) return page(this.notes(), this.cups.page());
    if (cell.state === 'failed' && isNoClan(cell.error)) return this.clanless();
    if (cell.state === 'failed') {
      return page(h1(S.title), this.notes(), plain(failed(cell.error)));
    }
    if (cell.value === undefined) return page(h1(S.title), plain(loading()));
    this.hail(cell.value);
    return this.inClan(cell.value);
  }

  /**
   * The clan hail (R550): a new application on this account's own clan.
   *
   * An `invite` clan writes an application an officer answers
   * (`metasrv/dbrepo`), so the arrival of an application is the hail. A
   * clan invite (R1306) goes the other way, to an account in no clan, and
   * the INVITES card of `pickup.ts` shows it without a cue.
   *
   * The first answer primes and sounds nothing, and the cue sounds once
   * however many applications arrived together. `html()` runs on every
   * redraw, so the set is what keeps a redraw silent.
   */
  private hail(my: MyClan): void {
    const ids = new Set(my.applications.map((a) => a.id));
    const known = this.hailed;
    this.hailed = ids;
    if (known === null) return;
    for (const id of ids) {
      if (!known.has(id)) { this.ctx.play(SFX_CLAN_HAIL); return; }
    }
  }

  /** The busy line and the last refusal, above whichever screen is drawn. */
  private notes(): string {
    let out = '';
    if (this.busy !== '') out += lede(S.busy_line({ label: this.busy }));
    if (this.notice !== '') out += lede(this.notice);
    if (this.err !== '') {
      const said = refusalFor(this.errCode, this.errField);
      out += errLine(said === '' ? this.err : `${said} — ${this.err}`);
    }
    return out;
  }

  // --- the section shell (§16.11, §16.16) --------------------------------

  /** The section the pane shows, from the sections `list` holds: the open
   *  one, else on a wide pane the first that is no `compact` row. Null is
   *  the tiles of a narrow pane. */
  private openOf(list: readonly ShellSection[], wide: boolean): ClanSection | null {
    this.firstId = this.nav.named((list.find((s) => !s.compact) ?? list[0])?.id) ?? 'members';
    const at = this.nav.shown(wide);
    if (list.some((s) => s.id === at)) return at;
    return wide ? this.firstId : null;
  }

  /** The panel in the shell: the heading, the sections, the open page and
   *  the column. `notes` stand at the head of every page and of the
   *  tiles, so a refusal shows where the reader pressed. */
  private shell(heading: string, sections: ShellSection[], open: ClanSection | null,
    page: (id: ClanSection) => ShellPage, large: boolean): string {
    const wide = this.ctx.wide();
    this.drawn = open;
    const shown = open ?? this.firstId;
    const p = page(shown);
    return shellHtml({
      title: S.title,
      heading,
      go: GO,
      back: BACK,
      sections,
      open,
      wide,
      large,
      page: { ...p, body: this.notes() + p.body },
      rootHead: this.notes(),
    });
  }

  // --- the screen for an account in no clan ------------------------------

  /** INVITES, FIND A CLAN and START ONE, with LAST SEASON'S SQUAD as the
   *  companion column. INVITES stands first while an invite waits: an
   *  answer to one is the shortest way into a clan (R1306). */
  private clanless(): string {
    const wide = this.ctx.wide();
    const invites = this.invites.inviteCount() ?? 0;
    const sections: ShellSection[] = [];
    if (invites > 0) {
      sections.push({
        id: 'invites', glyph: '✉', name: S.sec_invites, sum: S.sum_invites({ n: invites }), badge: invites,
      });
    }
    sections.push(
      { id: 'find', glyph: '⬢', name: S.sec_find, sum: findSummary(clanHoursCell(this.ctx)) },
      {
        id: 'start', glyph: '✦', name: S.sec_start,
        sum: hasFaction(this.faction) ? factionName(this.faction).toUpperCase() : '',
      },
    );
    const open = this.openOf(sections, wide);
    const squad = this.invites.squadBody();
    const companion: Companion | undefined = squad === ''
      ? undefined
      : { label: hubPickup.card_squad, html: squad, fold: 'below', narrow: 'below' };
    return this.shell(h1(S.title, S.title_sub_no_clan), sections, open, (id) => {
      switch (id) {
        case 'invites': return { body: this.invites.invitesCard(), companion };
        case 'start': return { body: plain(this.startBody()), companion };
        default: return { body: blocksHtml([this.hoursBlock(), this.clansBlock()]), companion };
      }
    }, false);
  }

  /** CLANS OF YOUR HOURS (R1908): the clans whose members state the
   *  reader's hours, in the server's order. Null when the read failed. */
  private hoursBlock(): Block | null {
    const read = clanHoursCell(this.ctx);
    if (read.state === 'failed') return null;
    const block = (aside: string, inner: string): Block => ({ title: S.card_hours, aside, inner });
    const m = read.value;
    if (m === undefined) return block('', pending(read));
    if (m.hours === '') {
      return block('', empty(S.hours_ask, '', { label: S.hours_ask_press, act: 'clan-hours-set' }));
    }
    const sub = S.hours_sub({ band: BAND_NAMES.get(m.hours) ?? m.hours.toUpperCase() });
    if (m.clans.length === 0) {
      return block(sub, empty(S.hours_none, S.hours_none_why({ min: CLAN_HOURS_MIN_MEMBERS })));
    }
    const more = m.total - m.clans.length;
    return block(sub,
      lede(S.hours_lede({ min: CLAN_HOURS_MIN_MEMBERS }))
      + m.clans.map((c) => this.hoursRow(c)).join('')
      + (more > 0 ? lede(S.hours_more({ n: more })) : ''));
  }

  /** One clan of the hours match: the name and the tag in the ink of its
   *  faction, the member count, and the control of the browse row. */
  private hoursRow(c: Clan): string {
    const ink = hasFaction(c.faction) ? ` style="color:${hex(factionColor(c.faction!))}"` : '';
    return this.clanLine(c, ' chours', ink) + ctlRow(this.applyControl(c, 'hours')) + this.stepOf(c, 'hours');
  }

  /** The head line of one clan row: the name and the tag, the cup mark
   *  beside the tag of the clan that holds it (card 09, R1634), and the
   *  member count. `cls` adds a class to the row, and `ink` is the style
   *  attribute of the name. */
  private clanLine(c: Clan, cls: string, ink: string): string {
    const mark = cupMarkFor(c.tag, 12, this.ctx.now());
    return `<div class="row${cls}"><span class="k"${ink}>${esc(`${c.name} [${c.tag}]`)}${mark}</span>`
      + `<span class="v ${isFull(c) ? 'bad' : 'acc'}">${esc(S.count_of({ have: c.members, cap: c.maxMembers }))}</span></div>`;
  }

  /** The control of one clan row: what this clan does with an application.
   *  A `closed` clan gets no press, and the server refuses an apply that
   *  reaches it anyway. `from` names the list, so the step opens there. */
  private applyControl(c: Clan, from: 'hours' | 'browse'): string {
    const policy = policyOf(c);
    if (isFull(c)) return tag(S.pill_full);
    if (policy === 'closed') return tag(S.pill_closed);
    if (policy === 'open') {
      return button(S.pill_join, { act: 'clan-apply', data: { clan: String(c.id) } }, { variant: 'primary' });
    }
    const on = this.applying === c.id && this.applyIn === from;
    return button(S.pill_apply, { act: 'clan-apply-step', data: { clan: String(c.id), from } },
      { variant: 'primary', on });
  }

  /** The apply step under the row of an INVITE clan, in the list that
   *  opened it. */
  private stepOf(c: Clan, from: 'hours' | 'browse'): string {
    const open = policyOf(c) === 'invite' && !isFull(c)
      && this.applying === c.id && this.applyIn === from;
    return open ? this.applyStep(c.id) : '';
  }

  /** CLANS: the browse box, and the list it asks for with the apply
   *  controls. The box stands in the block of its list: the page title
   *  FIND A CLAN names the page, so the box takes no title of its own
   *  (R1945). */
  private clansBlock(): Block {
    const find = lede(S.find_lede_size({ max_members: CLAN_MAX_MEMBERS }))
      + (SQUAD_COUNT > 1 ? lede(S.find_lede_squads) : '')
      + lede(S.find_lede_faction)
      + '<div class="form">'
      + this.labelled('browse', CLAN_NAME_MAX, S.browse_label)
      + `<button type="button" data-act="clan-search">${S.browse_button}</button></div>`;
    return {
      title: S.card_clans,
      aside: this.query === '' ? S.browse_first_page : S.browse_matching({ query: this.query.toUpperCase() }),
      inner: find + this.browseList(),
    };
  }

  /** The list the browse box asks for, with the apply controls. */
  private browseList(): string {
    const list = this.ctx.data.cell<Clan[]>(
      `clans:${this.query}`, TTL_ON_OPEN, () => browseClans(this.query));
    if (list.value === undefined) return pending(list);
    if (list.value.length === 0) {
      return empty(this.query === '' ? S.browse_none_listed : S.browse_none_match,
        S.browse_none_why, { label: S.browse_none_press, act: 'clan-start' });
    }
    return list.value.map((c) => {
      // The banner comes first: a player who joins this clan takes it.
      // REPORT names the clan by its id (deployment.md §7.6).
      const key = `clan:${c.id}`;
      return this.clanLine(c, '', '') + ctlRow(factionLabel(c.faction) + this.applyControl(c, 'browse')
        + this.reports.control(key, { kind: 'clan', clan: c.id }))
        + this.stepOf(c, 'browse') + this.reports.html(key);
    }).join('');
  }

  /** START ONE: the founding form in place (R2155). */
  private startBody(): string {
    return lede(S.create_lede_free) + foundingBody(this.faction, this.foundForm());
  }

  /** The boxes of the founding form: the name, the tag, FOUND IT and its
   *  gate line. The founder must pick a faction. The server answers a
   *  create with no faction with 400, so the panel holds the control shut
   *  until the founder picks one, and it sends no call it knows the server
   *  refuses. */
  private foundForm(): string {
    const ready = hasFaction(this.faction) && this.busy === '';
    // The name box takes the wide share of the row, and the tag box is as
    // wide as the longest tag the rules allow (`--tag-ch`, R2155).
    return `<div class="form cf-names" style="--tag-ch: ${CLAN_TAG_MAX}">`
      + this.labelled('clan-name', CLAN_NAME_MAX, S.create_name_label, 'cf-fname')
      + this.labelled('clan-tag', CLAN_TAG_MAX, S.create_tag_label, 'cf-ftag')
      + `<button type="button" data-act="clan-create"${ready ? '' : ' disabled'}>${S.create_button}</button>`
      + '</div>'
      + (hasFaction(this.faction) ? '' : lede(S.create_pick_faction));
  }

  /** Picks one faction card. A press on the picked card keeps it: a radio
   *  group never ends with no pick. */
  private pickFaction(pick: number): void {
    if (!hasFaction(pick) || pick === this.faction) return;
    this.faction = pick;
    this.ctx.play('sfx-menu-press');
    this.ctx.refresh();
  }

  // --- the screen for an account in a clan -------------------------------

  /** The sections of a clan, each with its summary and its count. */
  private clanSections(my: MyClan, manage: boolean): ShellSection[] {
    const apps = manage ? my.applications.length : 0;
    const out: ShellSection[] = [
      {
        id: 'chat', glyph: '✉', name: S.sec_chat, sum: this.chatSum(), badge: this.newLines(),
        wideTile: true, compact: true,
      },
      {
        id: 'members', glyph: '☰', name: S.sec_members,
        sum: apps > 0
          ? S.sum_members_apply({ have: my.members.length, cap: my.clan.maxMembers, n: apps })
          : S.count_of({ have: my.members.length, cap: my.clan.maxMembers }),
        badge: apps,
      },
      { id: 'war', glyph: '⚑', name: S.sec_war, sum: this.warSum() },
      { id: 'cup', glyph: '✪', name: S.sec_cup, sum: this.cups.summary(my.clan.id) },
    ];
    if (manage) {
      // Each summary is the state word of its page (NOT SET, ON or OFF).
      out.push(
        { id: 'discord', glyph: '◎', name: S.sec_discord, sum: this.discord.state() },
        { id: 'relay', glyph: '→', name: S.sec_relay, sum: this.relay.state() },
      );
    }
    return out;
  }

  /** The CHAT summary: the count of new lines and the last line, or the
   *  last line alone. A chat with no line states none. */
  private chatSum(): string {
    const blocked = this.blockedIds();
    let last: ClanChatMessage | undefined;
    for (const e of this.state.entries) {
      if (e.kind === 'message' && !blocked.has(e.senderId)) last = e;
    }
    if (last === undefined) return '';
    const line = S.chat_line({ sender: last.sender, body: chatText(last, last.body, storedChatFilterOn()) });
    const n = this.newLines();
    return n > 0 ? S.sum_chat_new({ n, line }) : line;
  }

  /** The CLAN WAR summary: the registration of the next run, or none
   *  until the read has answered. */
  private warSum(): string {
    const war = this.ctx.data.cell<ClanWar>(CLANWAR_CELL, TTL_ON_OPEN, getClanWar).value;
    if (war === undefined) return '';
    if (war.run === null) return S.sum_war_none;
    if (war.registration !== null) return S.sum_war_registered({ division: war.registration.division });
    return S.sum_war_open;
  }

  private inClan(my: MyClan): string {
    const manage = manages(my.role);
    const wide = this.ctx.wide();
    const sections = this.clanSections(my, manage);
    // The CUP page is a sub-page of CLAN CUP, so it holds that section open.
    const open = this.cups.pageOpen() ? 'cup' : this.openOf(sections, wide);
    // The chat sees the page: a CHAT page in view clears the CHAT count
    // before the rail states it.
    this.drawn = open;
    if (this.chatInView()) {
      this.seenArrived = this.arrived;
      sections[0] = { ...sections[0], sum: this.chatSum(), badge: 0 };
    }
    // A clan the server wrote before R240 carries no faction. Its heading
    // shows NO BANNER ON RECORD, and this line under it says why.
    const legacy = hasFaction(my.clan.faction) ? '' : lede(S.banner_none);
    // The chat is the companion of every page but its own: one chat box on
    // the screen, so the focus and the text of the box have one home.
    const chat: Companion = { label: S.card_clan_chat, html: this.chatBody(), fold: 'rail' };
    return this.shell(this.head(my) + legacy, sections, open, (id) => {
      switch (id) {
        case 'chat':
          return { body: this.chatPage() };
        case 'war':
          return { body: plain(this.clanWarBody(my)), companion: chat };
        case 'cup': {
          if (this.cups.pageOpen()) {
            const p = this.cups.pageParts();
            return { body: p.body, sub: { name: p.name, back: 'cup-back' }, companion: chat };
          }
          return { body: this.cups.card(my) + this.cups.record(), companion: chat };
        }
        case 'discord':
          return { body: this.discord.html(manage), companion: chat };
        case 'relay':
          return { body: this.relay.html(manage), companion: chat };
        default:
          return {
            body: blocksHtml([manage ? this.appsBlock(my) : null, this.rosterBlock(my), this.squadBlock()]),
            companion: chat,
          };
      }
    }, true);
  }

  /**
   * The heading of a clan's own screen: two lines at every width (R1562).
   *
   * The first line holds the section name, the clan name and the tag. The
   * name gives way first: it clips with an ellipsis, so a long name never
   * wraps the line and never pushes the tag off it. The second line holds
   * the member count and the banner the clan flies. The banner is part of
   * the heading and not of a card, so every member sees it, a plain member
   * too, and it cannot read as one more join-policy choice (R257).
   *
   * The first line is the `h1` of the page (R2048): a heading holds no
   * block, so the box of both lines stays a `div`.
   */
  private head(my: MyClan): string {
    const members = S.head_members({ have: my.members.length, cap: my.clan.maxMembers });
    // The cup mark stands beside the tag, and the cup title on a third
    // line, while the clan holds the cup mark (card 10, R1634).
    return '<div class="h1 clanhead">'
      + `<h1 class="l1"><span class="lb">${esc(S.title)}</span>`
      + `<span class="nm">${esc(my.clan.name)}</span>`
      + `<span class="tg">[${esc(my.clan.tag)}]</span>${cupMarkFor(my.clan.tag, 14, this.ctx.now())}</h1>`
      + `<div class="l2"><span class="ct">${esc(members)}</span>`
      + `${factionLabel(my.clan.faction)}</div>${this.cups.titleLine()}</div>`;
  }

  /** The CHAT page: the reach of the chat, the log and the box, with no
   *  card round them, because the page title names the chat (R1945).
   *  While the chat relay of the clan is on, every member sees the
   *  → DISCORD mark over them (R1641). */
  private chatPage(): string {
    return plain(figLine(S.chat_more) + this.relayedLog());
  }

  /** LAST SEASON'S SQUAD under MEMBERS, or null while there is nothing to
   *  show (R1306). */
  private squadBlock(): Block | null {
    const inner = this.invites.squadBody();
    return inner === '' ? null : { title: hubPickup.card_squad, aside: hubPickup.squad_more, inner };
  }

  /** The chat in the companion column: the log and the box with no card
   *  round it, under the → DISCORD mark while the relay is on. */
  private chatBody(): string {
    return `<div class="clanchat">${this.relayedLog()}</div>`;
  }

  /** The log under the → DISCORD mark while the relay is on. */
  private relayedLog(): string {
    return (this.state.relay ? ctlRow(relayTag()) : '') + this.chatLog();
  }

  /** The state of the socket, the log and the box. The socket's own state
   *  is drawn as it is. */
  private chatLog(): string {
    const s = this.state;
    let head = '';
    if (s.stopped !== null) {
      head = empty(...STOP_LINES[s.stopped]);
    } else if (this.chat === null) {
      head = empty(S.chat_not_open, S.chat_not_open_why);
    } else if (!s.connected) {
      head = empty(S.chat_not_connected,
        this.chat.retries > 0
          ? S.chat_retrying({ n: this.chat.retries })
          : S.chat_dialling);
    }
    if (s.lastError !== null) {
      head += `<div class="err">${esc(s.lastError.message)}</div>`;
    }

    let body: string;
    if (s.entries.length === 0) {
      body = empty(S.chat_empty);
    } else {
      const blocked = this.blockedIds();
      // The chat time stamp and the word filter of SOCIAL (R1791) are
      // read once for the log, not once for each line.
      const stamp = storedChatTimeOn();
      const filter = storedChatFilterOn();
      body = `<div class="chat">${s.entries.map((e) => this.chatLine(e, blocked, stamp, filter)).join('')}</div>`;
    }

    // The server's own cap is longer (`CHAT_BODY_LIMIT`), so the box is
    // the stricter of the two, and a player learns the limit from the box
    // and never from an error frame.
    const box = '<div class="chatbox">'
      + this.box('chat', CHAT_MAX_LEN, S.chat_placeholder)
      + `<button type="button" data-act="chat-send"${s.connected ? '' : ' disabled'}>`
      + `${S.chat_send}</button></div>`;
    return head + body + box;
  }

  /**
   * One line of the log: a gap mark, or a message. Another member's line
   * carries one small control, which opens REPORT and BLOCK under the line
   * (deployment.md §7.6). A line of a deleted account carries no public
   * id and no control, and a line of a blocked account draws nothing.
   * `stamp` says whether the line wears its time stamp, and `filter`
   * whether the word filter runs on its body.
   */
  private chatLine(e: ClanChatEntry, blocked: ReadonlySet<string>, stamp: boolean, filter: boolean): string {
    if (e.kind === 'gap') {
      return `<div class="chatmark">${S.chat_gap({ missed: e.missed })}</div>`;
    }
    if (blocked.has(e.senderId)) return '';
    const mine = e.senderId !== '' && e.senderId === this.state.selfId;
    const other = !mine && e.senderId !== '';
    const picked = other && this.linePick === e.seq;
    // The chat time stamp of SOCIAL (R1791): the time the line was sent.
    let out = `<div class="msg">${chatStampHtml(e.at, stamp)}`
      + `<span class="who${mine ? ' me' : ''}">${esc(e.sender)}</span>`
      + `<span class="b">${esc(chatText(e, e.body, filter))}</span>`
      + `<span class="t">${esc(formatAgo(this.ctx.now(), e.at))}</span>`
      + (other ? button(S.chat_actions, { act: 'chat-pick', data: { idx: String(e.seq) } }, { size: 'compact', on: picked }) : '')
      + '</div>';
    if (picked) {
      const key = `chat:${e.seq}`;
      out += ctlRow(this.reports.control(key, { kind: 'chat', seq: e.seq })
        + button(S.chat_block, { act: 'chat-block', data: { id: e.senderId } }, { variant: 'danger', size: 'compact' }))
        + this.reports.html(key);
    }
    return out;
  }

  /**
   * The context of one squad, for its roster heading: how full it is, the
   * world its seat holds and its Honour. A squad the batch has not seated
   * says so. Honour is the figure the batch ranks groups by; a server that
   * sends none gets no figure, because the panel invents no standing.
   */
  private squadLine(s: ClanSquad | undefined): string {
    const parts = [this.held(s)];
    const seat = s?.seat;
    parts.push(seat === undefined
      ? S.squad_not_seated
      : `${seat.world.toUpperCase()} · ${seat.region.toUpperCase()}`);
    if (s?.honour !== undefined) parts.push(`${S.row_honour} ${groupDigits(s.honour)}`);
    return parts.join(' · ');
  }

  /** The members the CLAN WAR card shows as picked. */
  private warPicked(my: MyClan, war: ClanWar): Set<string> {
    if (this.warPick !== null) return this.warPick;
    if (war.registration !== null) return new Set(war.registration.members);
    return this.warAll(my, war);
  }

  /** Every member the run can take: a list holds `divisionMax` at most, and a
   *  clan above a lowered figure holds more members than that (R2974). */
  private warAll(my: MyClan, war: ClanWar): Set<string> {
    return new Set(my.members.slice(0, war.divisionMax).map((m) => m.id));
  }

  /** The division a count takes, by the rule the server sent. */
  private divisionOf(war: ClanWar, count: number): number {
    const step = war.divisionStep > 0 ? war.divisionStep : 1;
    const d = Math.ceil(count / step) * step;
    return Math.min(Math.max(d, war.divisionMin), war.divisionMax);
  }

  /**
   * The CLAN WAR page (numbers.md §14.4, R1263, R1264): the next run, the
   * registration and its outcome, and for an owner or an officer before the
   * cut-off the member pick with REGISTER and WITHDRAW. The page title
   * names it, so it draws no card (R1945).
   */
  private clanWarBody(my: MyClan): string {
    const cell = this.ctx.data.cell<ClanWar>(CLANWAR_CELL, TTL_ON_OPEN, getClanWar);
    if (cell.state === 'failed') return failed(cell.error);
    const war = cell.value;
    if (war === undefined) return loading();
    // The clan's Clan Honour stands first in every state of the card: it
    // is what the clan's past runs paid, with or without a next run (R1512).
    const honour: Row = [S.cw_row_honour, S.cw_honour_value({ n: groupDigits(my.clan.honourClanwar ?? 0) }), 'gold'];
    const run = war.run;
    if (run === null) {
      return rows([honour]) + empty(S.cw_no_run, S.cw_no_run_why);
    }
    const now = this.ctx.now();
    const list: Row[] = [honour, [S.cw_row_run, run.label, 'acc']];
    const opens = secondsUntil(now, run.opensAt);
    if (opens > 0) list.push([S.cw_row_opens, formatSpan(opens)]);
    if (run.open) list.push([S.cw_row_cutoff, formatSpan(secondsUntil(now, run.cutoffAt)), 'gold']);
    const reg = war.registration;
    if (reg !== null) {
      list.push([S.cw_row_registered, S.count_of({ have: reg.count, cap: war.divisionMax })]);
      list.push([S.cw_row_division, String(reg.division), 'gold']);
    }
    let inner = rows(list) + lede(reg === null ? S.cw_not_registered
      : reg.outcome === 'placed' ? S.cw_outcome_placed({ division: reg.plays, banner: reg.banner })
        : OUTCOME_LINES.get(reg.outcome) ?? S.cw_outcome_waiting);
    if (!run.open) return inner + lede(S.cw_closed);
    if (!war.canRegister) return inner + lede(S.cw_officer_only);

    const picked = this.warPicked(my, war);
    inner += lede(S.cw_pick)
      + ctlRow(button(S.cw_pick_all, { act: 'cw-all' }) + button(S.cw_pick_none, { act: 'cw-none' }))
      // The roster is several saved values: ticked pills that wrap (R2011).
      + ctlRow(pillMulti(my.members.map((m) => ({ id: m.id, label: m.name })), picked, 'cw-pick',
        { label: S.cw_pick, wrap: true }))
      + lede(S.cw_pick_count({ count: picked.size, division: this.divisionOf(war, picked.size) }));
    const controls = button(reg === null ? S.cw_register : S.cw_update, { act: 'cw-register' }, { variant: 'primary' })
      + (reg === null ? '' : this.armed('cw-withdraw', S.cw_withdraw, S.cw_withdraw_armed, 'cw-withdraw'));
    return inner + ctlRow(controls);
  }

  /** How full one squad is. A squad the answer left out carries no cap the
   *  panel can print, so the row says only that the squad holds nobody. The
   *  panel invents no figure. */
  private held(s: ClanSquad | undefined): string {
    if (s === undefined || s.members === 0) return S.nobody_yet;
    return S.count_of({ have: s.members, cap: s.maxMembers });
  }

  /**
   * The applications an officer or the owner answers, and the policy that
   * decides whether there are any (R240).
   *
   * The policy control lives here and not on the roster, because the policy
   * is the rule this card runs under. The card itself is drawn for an
   * officer and the owner only, so the control carries the same gate.
   */
  private appsBlock(my: MyClan): Block {
    const policy = policyOf(my.clan);
    // The join policy is one saved value, so a pill group and never a tab
    // strip (R2011): the label names the policy, and the empty state under
    // it says what the policy that runs does (R1562).
    let inner = ctlRow(pillGroup(JOIN_POLICIES.map((p) => ({ id: p, label: policyWords(p).label })),
      policy, 'clan-policy', { label: S.card_applications }));

    if (my.applications.length === 0) {
      inner += empty(S.apps_none, policyWords(policy).none);
    } else {
      inner += my.applications.map((a) => {
        const row: Row[] = [[a.name, formatAgo(this.ctx.now(), a.createdAt), 'gold']];
        const note = (a.note ?? '').trim();
        return rows(row)
          + (note === '' ? '' : lede(note))
          + ctlRow(
            button(S.apps_approve, { act: 'clan-approve', data: { app: String(a.id) } }, { variant: 'primary' })
            + button(S.apps_refuse, { act: 'clan-refuse', data: { app: String(a.id) } }));
      }).join('');
    }
    return { title: S.card_applications, aside: S.apps_more({ count: my.applications.length }), inner };
  }

  /**
   * The roster: every member, under the squad they hold, with the controls
   * the caller's own role carries.
   *
   * The squad control lives here, beside the role controls, because both act
   * on one person. A squad is a plan and not a rank, so an officer moves any
   * member between squads, the owner included, and the caller moves itself.
   * The role controls keep the stricter gate of their own.
   *
   * The controls are behind an EDIT control on the row (R257). The role is a
   * plain value again: a rank does not look like a control, and a player
   * never found the pick while the rank carried it.
   */
  private rosterBlock(my: MyClan): Block {
    const selfId = this.ctx.me().account.id;
    const owner = my.role === 'owner';
    const manage = manages(my.role);
    const byIdx = new Map(my.squads.map((s) => [s.idx, s]));
    const memberRow = (m: ClanMember): string => {
      const isSelf = m.id === selfId;
      const canAct = manage && !isSelf && outranks(my.role, m.role);
      const cls = isSelf ? ' acc' : '';
      // The move offers a squad the member does not hold and that has room.
      const moves = manage ? SQUAD_IDX.filter((idx) => {
        const s = byIdx.get(idx);
        return idx !== m.squad && (s === undefined || s.members < s.maxMembers);
      }) : [];
      // The EDIT control shows while the caller manages the clan and a
      // control stands behind it. What is behind it differs: the move is
      // open on everybody, and the role and the removal keep their own gate.
      // The caret and the `on` state say which row is open, because one row
      // is open at a time.
      const editable = moves.length > 0 || canAct;
      const open = editable && this.pick === m.id;
      const edit = editable
        ? button(open ? S.edit_open : S.edit_shut, { act: 'clan-pick', data: { id: m.id } }, { size: 'compact', on: open })
        : '';
      const report = isSelf ? '' : this.reports.control(`member:${m.id}`, { kind: 'account', account: m.id });
      let out = `<div class="row${manage ? ' act' : ''}">`
        + `<span class="k">${esc(m.name)}${isSelf ? S.row_self_suffix : ''}</span>`
        + `<span class="v${cls}">${esc(roleLabel(m.role))}</span>${edit}${report}</div>`
        + (isSelf ? '' : this.reports.html(`member:${m.id}`));
      if (open) {
        const move = moves.map((idx) => button(`→ ${idx}`, { act: 'clan-assign', data: { idx: String(idx), id: m.id } },
          { variant: 'primary' })).join('');
        let role = '';
        let gone = '';
        if (canAct) {
          if (owner) {
            if (m.role === 'member') {
              role = button(S.make_officer, { act: 'clan-role', data: { id: m.id, role: 'officer' } },
                { variant: 'primary' });
            } else if (m.role === 'officer') {
              role = button(S.make_member, { act: 'clan-role', data: { id: m.id, role: 'member' } });
            }
            gone += this.armed(`hand:${m.id}`, S.hand_over, S.hand_over_armed, 'clan-role', { id: m.id, role: 'owner' });
          }
          gone += this.armed(`remove:${m.id}`, S.remove, S.remove_armed, 'clan-remove', { id: m.id });
        }
        out += editRow(move, role, gone);
      }
      return out;
    };

    // The members, under the squad they hold. A squad with nobody in it still
    // draws its heading: a clan holds each squad of the figure whether or not
    // anybody sits in it, which is what the squad card says as well. An old
    // squad above the figure draws only while it holds a member, so nobody
    // falls out of the roster and an empty old squad is never drawn (R2974).
    const groups = new Map<number, ClanMember[]>();
    for (const m of this.sorted(my.members)) {
      const idx = m.squad >= 1 ? m.squad : 0;
      const list = groups.get(idx);
      if (list === undefined) groups.set(idx, [m]); else list.push(m);
    }
    const old = [...groups.keys()].filter((idx) => idx > SQUAD_COUNT).sort((a, b) => a - b);
    const shown = [...SQUAD_IDX, ...old];
    // One squad reads as one list: its heading keeps the count, the seat and
    // the Honour, and it needs no number.
    let inner = SQUAD_COUNT > 1 ? lede(S.squads_plan) : '';
    for (const idx of shown) {
      const list = groups.get(idx) ?? [];
      const head = shown.length === 1 ? S.squad_single : S.squad_n({ idx });
      inner += rows([[head, this.squadLine(byIdx.get(idx)), 'acc']]);
      inner += list.length === 0 ? rows([[S.nobody_yet, '']]) : list.map(memberRow).join('');
    }
    // A member with no squad is what a server older than this build answers.
    // The group is drawn only in that case, so nobody ever falls out of the
    // roster.
    const loose = groups.get(0) ?? [];
    if (loose.length > 0) {
      inner += rows([[S.no_squad, `${loose.length}`, 'bad']])
        + loose.map(memberRow).join('');
    }

    const leave = ctlRow(this.armed('leave', S.leave_clan, S.leave_clan_armed, 'clan-leave'));
    return { title: S.card_roster, aside: S.roster_more({ count: my.members.length }), inner: inner + leave };
  }

  /** The owner first, then the officers, then the members, and by name
   *  inside a role. The order is fixed, so a redraw never shuffles it. */
  private sorted(list: ClanMember[]): ClanMember[] {
    return [...list].sort((a, b) => (
      rankOf(a.role) - rankOf(b.role) || a.name.localeCompare(b.name)
    ));
  }

  /** One destructive control of this panel (R696), on the panel's gate and
   *  with the armed face that names its act (R2069); `armedPress` is its
   *  press. */
  private armed(key: string, label: string, armFace: string, act: string, data?: Record<string, string>): string {
    return armedButton(this.gate, key, label, armFace, { act, data });
  }

  /** The clan and the Clan War as the cache holds them now, for a press
   *  that acts on what the pane drew; undefined while either read has not
   *  answered. */
  private clanAndWar(): { my: MyClan; war: ClanWar } | undefined {
    const my = this.ctx.data.peek<MyClan>(CLAN_CELL).value;
    const war = this.ctx.data.peek<ClanWar>(CLANWAR_CELL).value;
    return my === undefined || war === undefined ? undefined : { my, war };
  }

  /** What one box holds, trimmed. */
  private typed(name: string): string {
    return (this.text[name] ?? '').trim();
  }

  /** One text box, with what `this.text` holds for it. A `label` makes it
   *  a labelled box of the DISCORD or CHAT RELAY card (R1738). */
  private box(name: string, max: number, placeholder: string, label = ''): string {
    if (label === '') return textBox(name, `type="text" maxlength="${max}"`, placeholder, this.text[name] ?? '');
    return this.labelled(name, max, label, '', placeholder);
  }

  /**
   * The apply step of one INVITE clan (R1742): the note box to its
   * officers, SEND and CANCEL. The note is asked here, after the player
   * picked the clan it goes to.
   */
  private applyStep(clanId: number): string {
    const held = this.busy === '' ? '' : ' disabled';
    return '<div class="form apply">'
      + this.labelled('note', CLAN_NOTE_RUNES, S.note_label)
      + `<button type="button" data-act="clan-apply" data-clan="${clanId}"${held}>${S.apply_send}</button>`
      + button(S.apply_cancel, { act: 'clan-apply-step', data: { clan: '0' } })
      + '</div>';
  }

  /** Sends one application, or joins an OPEN clan. The note goes only to
   *  the clan whose apply step stands open. */
  private applyTo(clanId: number): void {
    if (clanId <= 0) return;
    const note = this.applying === clanId ? this.typed('note') : '';
    this.call(S.busy_applying, () => applyToClan(clanId, note),
      (out) => {
        this.text.note = '';
        this.applying = 0;
        // An `open` clan takes the applicant in inside the call (R240).
        // The account is in a clan from this instant, so the panel says
        // nothing about waiting and opens the chat the clanless socket
        // never had.
        if (out.joined) {
          this.ctx.data.dropPrefix('clans:');
          this.reopenChat();
          return;
        }
        this.notice = S.notice_applied;
      });
  }

  /** One text box with a visible label over it (R1738), and with no
   *  placeholder on the forms of an account in no clan. The CLAN panel is
   *  the one panel that draws these boxes, and the box names are unique on
   *  the page, so the id is too. */
  private labelled(name: string, max: number, label: string, cls = '', placeholder = ''): string {
    const id = `hub-clan-${name}`;
    return field(id, label,
      textBox(name, `id="${id}" type="text" maxlength="${max}"`, placeholder, this.text[name] ?? ''), cls);
  }

  // --- the block (deployment.md §7.6) -----------------------------------

  /** Every public id whose lines the card hides: the ones this session
   *  blocked and the stored list, once it has answered. */
  private blockedIds(): Set<string> {
    const read = blocksCell(this.ctx);
    if (read.state !== 'ready' || read.value === undefined) return this.blocked;
    return new Set([...this.blocked, ...read.value.blocks.map((b) => b.id)]);
  }

  /** BLOCK beside a chat line. The socket drops the account's next lines;
   *  the lines on the card go at once. */
  private block(publicId: string): void {
    if (publicId === '') return;
    this.call(S.chat_block, () => putBlock(publicId), () => {
      this.blocked.add(publicId);
      this.linePick = 0;
      this.ctx.data.drop(BLOCKS_CELL);
      this.notice = S.chat_blocked;
    });
  }

  // --- presses and boxes -------------------------------------------------

  act(action: string, el: HTMLElement): void {
    if (this.invites.act(action, el)) return;
    if (this.discord.act(action, el)) return;
    if (this.relay.act(action, el)) return;
    if (this.cups.act(action, el)) return;
    if (this.reports.act(action, el)) return;
    // A rail row, a tile or ‹ CLAN. The chat socket belongs to the panel
    // and not to a page, so a change of page leaves it as it is; a rail
    // press puts every armed control at rest.
    if (shellPress(this.nav, this.ctx, SHELL, action, el, (to) => {
      this.cups.hide();
      if (to !== null) this.gate.clear(false);
    })) return;
    const id = el.dataset.id ?? '';
    const idx = Number.parseInt(el.dataset.idx ?? '0', 10);
    const app = Number.parseInt(el.dataset.app ?? '0', 10);
    switch (action) {
      case 'clan-search':
        this.query = this.typed('browse');
        this.ctx.play('sfx-menu-press');
        this.ctx.refresh();
        return;
      case 'clan-apply':
        this.applyTo(Number.parseInt(el.dataset.clan ?? '0', 10));
        return;
      case 'clan-apply-step': {
        // APPLY opens the step of its clan, and a second press or CANCEL
        // closes it. The note stays with the step it was typed in.
        const clanId = Number.parseInt(el.dataset.clan ?? '0', 10);
        const from = el.dataset.from === 'hours' ? 'hours' : 'browse';
        const next = clanId === this.applying && from === this.applyIn ? 0 : Math.max(clanId, 0);
        if (next !== this.applying || from !== this.applyIn) this.text.note = '';
        this.applying = next;
        this.applyIn = from;
        this.ctx.play(next === 0 ? 'sfx-menu-back' : 'sfx-menu-press');
        this.ctx.refresh();
        return;
      }
      case 'clan-hours-set':
        // The ask of a reader with no stated hours opens SETTINGS · PLAY,
        // where the STATED HOURS stand (R1908).
        this.ctx.play('sfx-menu-press');
        this.ctx.go('settings', 'play');
        return;
      case 'clan-start':
        // The press of the empty list opens START ONE, and the next wire
        // puts the focus in its name box.
        this.nav.go('start', this.ctx.wide());
        this.focusName = true;
        this.ctx.play('sfx-menu-press');
        this.ctx.refresh();
        return;
      case 'clan-create':
        this.founds();
        return;
      case FACTION_ACT:
        this.pickFaction(Number.parseInt(el.dataset.id ?? '0', 10));
        return;
      case 'clan-policy': {
        const policy = (el.dataset.id ?? '') as JoinPolicy;
        if (!JOIN_POLICIES.includes(policy)) return;
        this.call(S.busy_policy, () => setJoinPolicy(policy));
        return;
      }
      case 'clan-approve':
        if (app > 0) this.call(S.busy_approve, () => approveApplication(app));
        return;
      case 'clan-refuse':
        if (app > 0) this.call(S.busy_refuse, () => refuseApplication(app));
        return;
      case 'clan-pick':
        this.pick = this.pick === id ? '' : id;
        this.gate.clear(false);
        this.ctx.play('sfx-menu-press');
        this.ctx.refresh();
        return;
      case 'clan-role': {
        const role = (el.dataset.role ?? 'member') as ClanRole;
        // Handing the clan over cannot be undone by the person who did it,
        // so it takes the second press. A promotion is reversible and it
        // does not.
        if (role === 'owner' && !armedPress(this.ctx, this.gate, `hand:${id}`)) return;
        this.call(S.busy_role, () => setRole(id, role), () => { this.pick = ''; });
        return;
      }
      case 'clan-remove':
        if (id === '' || !armedPress(this.ctx, this.gate, `remove:${id}`)) return;
        this.call(S.busy_remove, () => removeMember(id), () => { this.pick = ''; });
        return;
      case 'clan-leave':
        if (!armedPress(this.ctx, this.gate, 'leave')) return;
        this.call(S.busy_leave, () => leaveClan(), () => {
          this.ctx.data.dropPrefix('clans:');
        });
        return;
      case 'cw-pick': {
        const cw = this.clanAndWar();
        if (cw === undefined || id === '') return;
        const { my, war } = cw;
        const next = new Set(this.warPicked(my, war));
        if (next.has(id)) next.delete(id);
        else if (next.size < war.divisionMax) next.add(id);
        this.warPick = next;
        this.ctx.play('sfx-menu-press');
        this.ctx.refresh();
        return;
      }
      case 'cw-all':
      case 'cw-none': {
        const cw = this.clanAndWar();
        if (cw === undefined) return;
        this.warPick = action === 'cw-all' ? this.warAll(cw.my, cw.war) : new Set();
        this.ctx.play('sfx-menu-press');
        this.ctx.refresh();
        return;
      }
      case 'cw-register': {
        const cw = this.clanAndWar();
        if (cw === undefined) return;
        const members = [...this.warPicked(cw.my, cw.war)];
        if (members.length === 0) {
          this.ctx.play('sfx-menu-error');
          return;
        }
        this.call(S.busy_register, () => registerClanWar(members), () => {
          this.warPick = null;
          this.ctx.data.drop(CLANWAR_CELL);
        });
        return;
      }
      case 'cw-withdraw':
        if (!armedPress(this.ctx, this.gate, 'cw-withdraw')) return;
        this.call(S.busy_withdraw, () => withdrawClanWar(), () => {
          this.warPick = null;
          this.ctx.data.drop(CLANWAR_CELL);
        });
        return;
      case 'clan-assign':
        if (idx <= 0 || id === '') return;
        this.call(S.busy_assign, () => assignSquad(idx, id), () => { this.pick = ''; });
        return;
      case 'chat-pick':
        this.linePick = this.linePick === idx ? 0 : idx;
        this.ctx.play('sfx-menu-press');
        this.ctx.refresh();
        return;
      case 'chat-block':
        this.block(id);
        return;
      case 'chat-send':
        this.send();
        return;
      default:
    }
  }

  /**
   * Founds the clan the two boxes name, under the faction the founder
   * picked. A create with no faction is not sent: the server refuses it with
   * 400, and the panel holds a refusal it can see coming.
   */
  private founds(): void {
    const name = this.typed('clan-name');
    const tag = this.typed('clan-tag');
    const faction = this.faction;
    if (name === '' || tag === '' || !hasFaction(faction)) {
      this.ctx.play('sfx-menu-error');
      return;
    }
    this.call(S.busy_create, () => createClan(name, tag, faction), () => {
      this.text['clan-name'] = '';
      this.text['clan-tag'] = '';
      this.faction = 0;
      // The account is in a clan now, and the old socket stopped on 4101. A
      // clan with a dead chat is not the answer.
      this.reopenChat();
    });
  }

  /**
   * The boxes. Only an `<input>` is wired here: every button acts through the
   * shell's own press router. The text lives in `this.text` and the markup
   * carries it back as a value, because the pane is rewritten whenever the
   * markup changes.
   */
  wire(root: HTMLElement): void {
    this.nav.scroll(root);
    // The report form's note box first: the loop below skips a box that
    // is already wired, so the note stays the form's own.
    this.reports.wire(root);
    if (this.focusName) {
      // The box stands in the pane, so the wire looks for it from the
      // pane and never from the document.
      const box = root.querySelector<HTMLInputElement>('input[data-in="clan-name"]');
      if (box !== null) {
        this.focusName = false;
        box.focus();
      }
    }
    this.measureColumn(root);
    wireFactionWalk(root, (pick) => this.pickFaction(pick));
    const boxes = root.querySelectorAll<HTMLInputElement>('input[data-in]');
    boxes.forEach((box) => {
      if (!wireOnce(box)) return;
      const name = box.dataset.in ?? '';
      box.addEventListener('input', () => { this.text[name] = box.value; });
      box.addEventListener('keydown', (ev) => {
        if (ev.key !== 'Enter') return;
        this.text[name] = box.value;
        this.submit(name);
      });
    });
  }

  /**
   * Reads whether the chat column stands. A container query of the
   * stylesheet decides it (§16.16), so the panel learns it from the layout
   * after the write; a column with no box on the screen has no client
   * rect. The CHAT count clears when the column comes into view.
   */
  private measureColumn(root: HTMLElement): void {
    const side = root.querySelector<HTMLElement>('.hs-row.large > .hs-side');
    const shown = side !== null && side.getClientRects().length > 0;
    if (shown === this.columnShown) return;
    this.columnShown = shown;
    if (this.chatInView() && this.seenArrived !== this.arrived) {
      this.seenArrived = this.arrived;
      this.ctx.refresh();
    }
  }

  /** What the Enter key does in each box. */
  private submit(name: string): void {
    switch (name) {
      case 'chat':
        this.send();
        return;
      case 'browse':
        this.query = this.typed('browse');
        this.ctx.refresh();
        return;
      case 'clan-name':
      case 'clan-tag':
        this.founds();
        return;
      case 'note':
        this.applyTo(this.applying);
        return;
      default:
    }
  }
}

/** Makes the CLAN panel. */
export function createClanPanel(ctx: PanelContext): Panel {
  return new ClanPanel(ctx);
}

/** The → DISCORD mark over the clan chat while the relay is on (R1641): a
 *  state, so a tag (R2011). The title says the whole sentence. */
function relayTag(): string {
  return `<span class="relaymark" title="${esc(RM.title)}">${tag(RM.mark, 'accent')}</span>`;
}
